import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

// ---------- Config ----------
const API_ORIGIN = process.env.REACT_APP_API_BASE || 'https://api.karrentals.ca';
const API_BASE   = `${API_ORIGIN}/shuttle`;

// Prefer env, fallback to hardcoded if you want
const STRIPE_PK =
  process.env.REACT_APP_STRIPE_PK ||
  "pk_live_51SMsOXRzZP1NlUlVSjfJmLnvldUQSC878zKIj5GBFfp3BvFhkSnQL1Gtv6FIW540pLfQej4nyDaLfMR6Aq2cZneH00AhCBEQPW";

const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// BC tax defaults
const GST_RATE = 0.05;
const PST_RATE = 0.07;

// ---------- Helpers ----------
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => Object.fromEntries(new URLSearchParams(search)), [search]);
}

const fmtWords = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  if (/^\d{2}:\d{2}/.test(hhmm)) return hhmm;
  const d = new Date(hhmm);
  if (isNaN(d.getTime())) return hhmm;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const fmtMoney = (cents, currency = 'CAD') => {
  if (typeof cents !== 'number') return '—';
  return (cents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

const fetchJson = async (url, options = {}, { timeoutMs = 10000 } = {}) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: ctrl.signal, ...options });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.error) msg = j.error;
      } catch {}
      throw new Error(msg);
    }
    return await res.json();
  } finally {
    clearTimeout(t);
  }
};

// ---------- Stripe Payment Box ----------
const PaymentBox = ({ clientSecret, amountCents, currency, onPaid }) => {
  if (!stripePromise) {
    return (
      <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
        Stripe not configured. Set <span className="font-mono">REACT_APP_STRIPE_PK</span>.
      </div>
    );
  }
  if (!clientSecret) {
    return (
      <div className="rounded-2xl border border-gray-200 p-4">Preparing payment…</div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: 'stripe' },
      }}
    >
      <PaymentInner amountCents={amountCents} currency={currency} onPaid={onPaid} />
    </Elements>
  );
};

const PaymentInner = ({ amountCents, currency, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState('');
  const [ready, setReady] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');

    if (!stripe || !elements) {
      setErr('Payment is still initializing. Please wait a moment and try again.');
      return;
    }

    setPaying(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErr(error.message || 'Payment failed.');
        return;
      }

      if (
        paymentIntent?.status === 'succeeded' ||
        paymentIntent?.status === 'processing' ||
        paymentIntent?.status === 'requires_capture'
      ) {
        onPaid?.(paymentIntent);
      } else {
        setErr(`Payment status: ${paymentIntent?.status || 'unknown'}`);
      }
    } catch (e2) {
      setErr(e2.message || 'Unexpected payment error.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement onReady={() => setReady(true)} />
      <button
        type="submit"
        disabled={!stripe || !elements || !ready || paying}
        className={`w-full rounded-full px-5 py-2 font-semibold text-white ${
          paying ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {paying ? 'Processing…' : `Pay ${fmtMoney(amountCents, currency)}`}
      </button>
      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
          {err}
        </div>
      )}
    </form>
  );
};

// ---------- Component ----------
const Checkout = () => {
  const q = useQuery();
  const navigate = useNavigate();

  const tripType       = String(q.trip_type || 'oneway').toLowerCase();
  const destinationId  = q.destination_id;
  const passengers     = Math.max(1, parseInt(q.passengers || '1', 10));

  const outboundDate   = tripType === 'oneway' ? q.service_date : q.outbound_date;
  const outboundTime   = tripType === 'oneway' ? q.time_slot    : q.outbound_time;
  const returnDate     = tripType === 'return' ? q.return_date  : null;
  const returnTime     = tripType === 'return' ? q.return_time  : null;

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [destinationMeta, setDestinationMeta] = useState(null);
  const [outboundSlot, setOutboundSlot]       = useState(null);
  const [returnSlot, setReturnSlot]           = useState(null);

  // Member toggle
  const [isMember, setIsMember] = useState(false);

  // Payment mode
  // - 'stripe'  => pay online
  // - 'manual'  => pay manually (fallback)
  const [payMode, setPayMode] = useState('stripe');

  // Contact + validation
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [touched, setTouched] = useState({ name: false, email: false, phone: false, otp: false });
  const [fieldErrs, setFieldErrs] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpMsg, setOtpMsg] = useState('');

  // Stripe
  const [clientSecret, setClientSecret] = useState('');
  const [creatingPI, setCreatingPI] = useState(false);

  const currency = 'CAD';

  const invalid =
    !destinationId ||
    !outboundDate ||
    !outboundTime ||
    (tripType === 'return' && (!returnDate || !returnTime));

  const getAvailability = async (destId, date, leg) =>
    fetchJson(`${API_BASE}/availability?destination_id=${encodeURIComponent(destId)}&date=${encodeURIComponent(date)}&leg=${encodeURIComponent(leg)}`);

  // Load availability
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        if (invalid) throw new Error('Missing required trip details. Please go back and search again.');

        const a1 = await getAvailability(destinationId, outboundDate, 'outbound');
        const dest = a1?.destination || null;
        if (!dest) throw new Error('Unable to load route details.');

        const out = (a1?.slots || []).find(s => s.time_slot === outboundTime && (s.seats_left ?? 0) > 0);
        if (!out) throw new Error('Selected outbound time is no longer available. Please search again.');

        let ret = null;
        if (tripType === 'return') {
          const a2 = await getAvailability(destinationId, returnDate, 'return');
          ret = (a2?.slots || []).find(s => s.time_slot === returnTime && (s.seats_left ?? 0) > 0);
          if (!ret) throw new Error('Selected return time is no longer available. Please search again.');
        }

        if (!ignore) {
          setDestinationMeta(dest);
          setOutboundSlot(out);
          setReturnSlot(ret);
        }
      } catch (e) {
        if (!ignore) setError(e.message || 'Failed to load checkout data.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [destinationId, outboundDate, outboundTime, returnDate, returnTime, tripType, invalid]);

  const roundTripUnitCents = Number(destinationMeta?.return_price_cents || 0);
  const oneWayUnitCents    = Number(outboundSlot?.price_cents || 0);
  const fareUnitCents      = tripType === 'return' ? roundTripUnitCents : oneWayUnitCents;

  const subtotalCents = useMemo(() => {
    if (!outboundSlot) return 0;
    return isMember ? 0 : fareUnitCents * passengers;
  }, [fareUnitCents, passengers, outboundSlot, isMember]);

  const gstCents = useMemo(() => (isMember ? 0 : Math.round(subtotalCents * GST_RATE)), [subtotalCents, isMember]);
  const pstCents = useMemo(() => (isMember ? 0 : Math.round(subtotalCents * PST_RATE)), [subtotalCents, isMember]);
  const totalCents = useMemo(() => subtotalCents + gstCents + pstCents, [subtotalCents, gstCents, pstCents]);

  const routeLabel = (meta) => meta?.name || `Destination #${destinationId}`;

  // ---------- Validators ----------
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9+\-() ]{7,20}$/;

  const validate = (c) => {
    const errs = {};
    if (!c.name || c.name.trim().length < 2) errs.name = 'Please enter your full name.';
    if (!c.email || !emailRegex.test(c.email)) errs.email = 'Enter a valid email address.';
    if (c.phone && !phoneRegex.test(c.phone)) errs.phone = 'Enter a valid phone or leave blank.';
    if (otpSent && !otpVerified) {
      if (!otpCode || otpCode.trim().length < 4) errs.otp = 'Enter the 4–8 digit code we emailed you.';
    }
    return errs;
  };

  useEffect(() => {
    setFieldErrs(validate(contact));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, otpCode, otpSent, otpVerified]);

  // ---------- Email OTP ----------
  const sendOtp = async () => {
    setOtpMsg('');
    if (!contact.email || !emailRegex.test(contact.email)) {
      setTouched((t) => ({ ...t, email: true }));
      setFieldErrs((e) => ({ ...e, email: 'Enter a valid email before requesting a code.' }));
      return;
    }
    try {
      await fetchJson(`${API_BASE}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: contact.email }),
      });
      setOtpSent(true);
      setOtpMsg('Verification code sent to your email.');
    } catch (e) {
      setOtpMsg(`Failed to send code: ${e.message}`);
    }
  };

  const verifyOtp = async () => {
    setOtpMsg('');
    try {
      await fetchJson(`${API_BASE}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: contact.email, otp: otpCode }),
      });
      setOtpVerified(true);
      setOtpMsg('Email verified ✔');
      setFieldErrs((prev) => {
        const copy = { ...prev };
        delete copy.otp;
        return copy;
      });
    } catch (e) {
      setOtpVerified(false);
      setOtpMsg(`Verification failed: ${e.message}`);
      setTouched((t) => ({ ...t, otp: true }));
      setFieldErrs((errs) => ({ ...errs, otp: 'Invalid or expired code.' }));
    }
  };

  const buildLegs = () => {
    const legs = [
      {
        leg: 'outbound',
        destination_id: Number(destinationId),
        service_date: String(outboundDate),
        time_slot: String(outboundTime),
        seats_reserved: Number(passengers),
      },
    ];
    if (tripType === 'return') {
      legs.push({
        leg: 'return',
        destination_id: Number(destinationId),
        service_date: String(returnDate),
        time_slot: String(returnTime),
        seats_reserved: Number(passengers),
      });
    }
    return legs;
  };

  const canSubmitContact =
    !validate(contact).name &&
    !validate(contact).email &&
    !validate(contact).phone &&
    (otpVerified || !otpSent);

  // ---------- Create PaymentIntent (only when: NOT member + payMode=stripe + total>0) ----------
  useEffect(() => {
    let ignore = false;

    (async () => {
      const shouldStripe = !isMember && payMode === 'stripe' && totalCents > 0 && destinationMeta;

      // Clear if not using stripe
      if (!shouldStripe) {
        setClientSecret('');
        setCreatingPI(false);
        return;
      }

      setCreatingPI(true);
      setClientSecret('');
      try {
        const resp = await fetchJson(`${API_BASE}/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount_cents: totalCents,
            currency: 'CAD',
            metadata: {
              trip_type: tripType,
              destination_id: String(destinationId),
              outbound_date: String(outboundDate),
              outbound_time: String(outboundTime),
              ...(tripType === 'return' ? { return_date: String(returnDate), return_time: String(returnTime) } : {}),
              passengers: String(passengers),
              customer_email: String(contact.email || ''),
            },
          }),
        });

        const cs = resp?.clientSecret || resp?.client_secret || '';
        if (!ignore) setClientSecret(cs);
      } catch (e) {
        if (!ignore) setError(`Failed to prepare payment: ${e.message}`);
      } finally {
        if (!ignore) setCreatingPI(false);
      }
    })();

    return () => { ignore = true; };
  }, [isMember, payMode, totalCents, destinationMeta, tripType, destinationId, outboundDate, outboundTime, returnDate, returnTime, passengers, contact.email]);

  // ---------- Finalize order ----------
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const finalizeOrder = async ({ paymentIntentId = null, member = false, manual = false } = {}) => {
    setSubmitting(true);
    setError('');
    setReceipt(null);
    try {
      const resp = await fetchJson(`${API_BASE}/order/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId,
          is_member: member,
          manual_payment: manual, // ✅ key
          contact: { name: contact.name, email: contact.email, phone: contact.phone || null },
          currency,
          legs: buildLegs(),
        }),
      });
      setReceipt(resp);
    } catch (e) {
      setError(e.message || 'Failed to finalize order');
    } finally {
      setSubmitting(false);
    }
  };

  // Stripe success callback
  const handlePaid = async (paymentIntent) => {
    await finalizeOrder({ paymentIntentId: paymentIntent?.id, member: false, manual: false });
  };

  // Member booking
  const handleMemberConfirm = async () => {
    if (!canSubmitContact) return;
    await finalizeOrder({ paymentIntentId: null, member: true, manual: false });
  };

  // Manual payment booking
  const handleManualConfirm = async () => {
    if (!canSubmitContact) return;
    await finalizeOrder({ paymentIntentId: null, member: false, manual: true });
  };

  const goBack = () => navigate(-1);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Checkout</h1>
          <button onClick={goBack} className="text-sm rounded-full border border-gray-300 px-3 py-1 hover:bg-gray-100">
            ← Back
          </button>
        </div>

        {loading && (
          <div className="rounded-2xl border border-gray-200 p-6 bg-white shadow-sm">Loading…</div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 p-6 bg-red-50 text-red-700 shadow-sm">{error}</div>
        )}

        {!loading && !error && outboundSlot && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Summary */}
            <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4">
                <div className="text-sm text-gray-500">Route</div>
                <div className="text-lg font-semibold text-gray-500">{routeLabel(destinationMeta)}</div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="font-semibold mb-1 text-gray-500">Outbound</div>
                  <div className="text-sm text-gray-600">{fmtWords(outboundDate)}</div>
                  <div className="text-sm text-gray-800">{fmtTime(outboundTime)}</div>
                </div>

                {tripType === 'return' && returnSlot && (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <div className="font-semibold mb-1">Return</div>
                    <div className="text-sm text-gray-600">{fmtWords(returnDate)}</div>
                    <div className="text-sm text-gray-800">{fmtTime(returnTime)}</div>
                  </div>
                )}
              </div>

              {/* Member toggle */}
              <div className="mt-6 rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                <input
                  id="member"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={isMember}
                  onChange={(e) => {
                    setIsMember(e.target.checked);
                    // if member, payment mode doesn't matter
                  }}
                />
                <label htmlFor="member" className="text-sm text-gray-700">
                  I’m a <span className="font-semibold">member</span> (no payment required)
                </label>
              </div>

              {/* Totals */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Passengers</div>
                  <div className="font-semibold text-gray-500">{passengers}</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Subtotal</div>
                  <div className="font-semibold text-gray-500">{fmtMoney(subtotalCents, currency)}</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">GST (5%)</div>
                  <div className="font-semibold text-gray-500">{fmtMoney(gstCents, currency)}</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">PST (7%)</div>
                  <div className="font-semibold text-gray-500">{fmtMoney(pstCents, currency)}</div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-lg font-semibold text-gray-900">{fmtMoney(totalCents, currency)}</div>
              </div>
            </div>

            {/* Contact + Payment */}
            <div className="space-y-6">
              {/* Contact */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-3 text-gray-500">Passenger details</h2>

                <label className="block text-sm mb-1 text-gray-500">Full name</label>
                <input
                  className={`w-full rounded-lg border px-3 py-2 mb-2 ${touched.name && fieldErrs.name ? 'border-red-400' : 'border-gray-300'}`}
                  value={contact.name}
                  onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  required
                />
                {touched.name && fieldErrs.name && <div className="text-xs text-red-600 mb-2">{fieldErrs.name}</div>}

                <label className="block text-sm mb-1 text-gray-500">Email</label>
                <div className="flex gap-2">
                  <input
                    className={`flex-1 rounded-lg border px-3 py-2 mb-2 ${touched.email && fieldErrs.email ? 'border-red-400' : 'border-gray-300'}`}
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    required
                  />
                  <button
                    type="button"
                    onClick={sendOtp}
                    className="whitespace-nowrap h-[38px] rounded-lg px-3 py-2 bg-gray-900 text-white disabled:bg-gray-400"
                    disabled={!contact.email}
                  >
                    {otpSent ? 'Resend code' : 'Send code'}
                  </button>
                </div>
                {touched.email && fieldErrs.email && <div className="text-xs text-red-600 mb-2">{fieldErrs.email}</div>}

                {otpSent && (
                  <>
                    <label className="block text-sm mb-1 text-gray-500">Email code</label>
                    <div className="flex gap-2">
                      <input
                        className={`flex-1 rounded-lg border px-3 py-2 mb-2 ${touched.otp && fieldErrs.otp ? 'border-red-400' : 'border-gray-300'}`}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, otp: true }))}
                        placeholder="Enter the 4–8 digit code"
                      />
                      <button
                        type="button"
                        onClick={verifyOtp}
                        className="whitespace-nowrap h-[38px] rounded-lg px-3 py-2 bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Verify
                      </button>
                    </div>
                    {otpMsg && <div className={`text-xs mb-2 ${otpVerified ? 'text-green-700' : 'text-gray-600'}`}>{otpMsg}</div>}
                    {touched.otp && fieldErrs.otp && <div className="text-xs text-red-600 mb-2">{fieldErrs.otp}</div>}
                  </>
                )}

                <label className="block text-sm mb-1 text-gray-500">Phone (optional)</label>
                <input
                  className={`w-full rounded-lg border px-3 py-2 mb-2 ${touched.phone && fieldErrs.phone ? 'border-red-400' : 'border-gray-300'}`}
                  value={contact.phone}
                  onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                  onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                />
                {touched.phone && fieldErrs.phone && <div className="text-xs text-red-600 mb-2">{fieldErrs.phone}</div>}
              </div>

              {/* Pay */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-3 text-gray-500">
                  {isMember ? 'Confirm booking' : 'Payment'}
                </h2>

                {!isMember && (
                  <div className="mb-4 rounded-xl border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Payment method</div>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        checked
                        readOnly
                      />
                      Pay online
                    </label>

                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-400 cursor-not-allowed">
                      <input
                        type="radio"
                        disabled
                      />
                      Pay manually (temporarily unavailable)
                    </label>
                  </div>
                )}

                {isMember ? (
                  <button
                    type="button"
                    onClick={handleMemberConfirm}
                    disabled={!canSubmitContact || submitting}
                    className={`w-full rounded-full px-5 py-2 font-semibold text-white ${submitting ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700'}`}
                  >
                    {submitting ? 'Confirming…' : 'Confirm Booking (Member – $0)'}
                  </button>
                ) : payMode === 'manual' ? (
                  <button
                    type="button"
                    onClick={handleManualConfirm}
                    disabled={!canSubmitContact || submitting}
                    className={`w-full rounded-full px-5 py-2 font-semibold text-white ${submitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {submitting ? 'Booking…' : `Book now — pay ${fmtMoney(totalCents, currency)} manually`}
                  </button>
                ) : (
                  <>
                    {!canSubmitContact && (
                      <div className="mb-3 text-xs text-gray-600">
                        Enter passenger details first (and verify email if you sent a code).
                      </div>
                    )}
                    {!canSubmitContact ? (
                      <button
                        type="button"
                        disabled
                        className="w-full rounded-full px-5 py-2 font-semibold text-white bg-gray-400"
                      >
                        Complete passenger details to pay
                      </button>
                    ) : (
                      <>
                        {creatingPI && <div>Preparing payment…</div>}
                        {!creatingPI && (
                          <PaymentBox
                            clientSecret={clientSecret}
                            amountCents={totalCents}
                            currency={currency}
                            onPaid={handlePaid}
                          />
                        )}
                      </>
                    )}
                  </>
                )}

                {submitting && <div className="text-sm text-gray-600 mt-3">Finalizing reservation…</div>}

                {receipt && (
                  <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                    Booking confirmed!
                    {receipt.confirmation_code && (
                      <div>Confirmation: <span className="font-mono">{receipt.confirmation_code}</span></div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Checkout;
